import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";

export interface LegendCode {
  mainCode: string;
  color: string;
}

interface DetectionLegendProps {
  codes: LegendCode[];
  hidden: Set<string>;
  onToggle: (mainCode: string) => void;
  onShowAll?: () => void;
}

/**
 * Colour legend with per-main-code show/hide toggles, shared by the inspection tabs
 * (mirrors the old demo HTML's main-code buttons). Hidden codes render desaturated
 * with a strike-through label. Excluded from PDF export via data-html2canvas-ignore.
 */
const DetectionLegend: React.FC<DetectionLegendProps> = ({
  codes,
  hidden,
  onToggle,
  onShowAll,
}) => {
  if (!codes.length) return null;

  return (
    <Box
      data-html2canvas-ignore
      sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", my: 1 }}
    >
      {codes.map(({ mainCode, color }) => {
        const isHidden = hidden.has(mainCode);
        return (
          <Tooltip key={mainCode} title={isHidden ? "Show" : "Hide"}>
            <Chip
              label={mainCode}
              size="small"
              onClick={() => onToggle(mainCode)}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                color: isHidden ? "text.disabled" : "#fff",
                backgroundColor: isHidden ? "transparent" : color,
                border: `1px solid ${color}`,
                opacity: isHidden ? 0.5 : 1,
                textDecoration: isHidden ? "line-through" : "none",
                "&:hover": { backgroundColor: isHidden ? "action.hover" : color },
              }}
            />
          </Tooltip>
        );
      })}
      {onShowAll && hidden.size > 0 && (
        <Chip
          label="Show all"
          size="small"
          variant="outlined"
          onClick={onShowAll}
          sx={{ cursor: "pointer" }}
        />
      )}
    </Box>
  );
};

export default DetectionLegend;
